/**
 * The small slice of Markdown a policy document actually uses: headings,
 * paragraphs, bullet lists, bold, and links.
 *
 * Written out rather than pulled in because the alternative is a Markdown
 * library plus an HTML sanitiser plus dangerouslySetInnerHTML — three
 * dependencies and an injection surface, to render text that only a superadmin
 * can write. This returns React elements, so there is no HTML string anywhere
 * in the path and nothing to sanitise.
 *
 * Anything it does not understand renders as plain text, which is the right
 * failure for a legal document: unstyled but complete beats a blank screen.
 */

/** **bold** and [text](href), left to right, no nesting. */
function inline(text, keyBase) {
  const out = []
  // The URL branch allows one level of balanced parentheses. A plain
  // [^)\s]+ stops at the first ")", which truncates any URL that contains a
  // pair — a Wikipedia article, an MSDN page — and leaks the leftover ")" into
  // the sentence as stray text.
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g
  let last = 0
  let m
  let i = 0

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    if (m[1] !== undefined) {
      out.push(<strong key={`${keyBase}-b${i}`}>{m[1]}</strong>)
    } else {
      // http(s) and mailto only. A policy has no reason to carry anything
      // else, and javascript: URLs are exactly what this guards against.
      const href = /^(https?:|mailto:)/i.test(m[3]) ? m[3] : null
      out.push(href
        ? <a key={`${keyBase}-a${i}`} href={href} target="_blank" rel="noopener noreferrer">{m[2]}</a>
        : m[2])
    }
    last = m.index + m[0].length
    i += 1
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

export default function Markdown({ source }) {
  if (!source) return null

  const blocks = []
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  let para = []
  let list = []

  const flushPara = () => {
    if (!para.length) return
    const key = `p${blocks.length}`
    blocks.push(<p key={key}>{inline(para.join(' '), key)}</p>)
    para = []
  }
  const flushList = () => {
    if (!list.length) return
    const key = `u${blocks.length}`
    blocks.push(
      <ul key={key}>
        {list.map((item, n) => <li key={n}>{inline(item, `${key}-${n}`)}</li>)}
      </ul>,
    )
    list = []
  }

  for (const raw of lines) {
    const line = raw.trimEnd()

    if (!line.trim()) { flushPara(); flushList(); continue }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line)
    if (heading) {
      flushPara(); flushList()
      const key = `h${blocks.length}`
      const level = heading[1].length
      const Tag = level === 1 ? 'h2' : level === 2 ? 'h3' : 'h4'
      blocks.push(<Tag key={key}>{inline(heading[2], key)}</Tag>)
      continue
    }

    const bullet = /^[-*]\s+(.*)$/.exec(line)
    if (bullet) { flushPara(); list.push(bullet[1]); continue }

    flushList()
    para.push(line.trim())
  }
  flushPara(); flushList()

  return <div className="prose">{blocks}</div>
}
